import { useEffect, useState, useCallback } from 'react';
import { useAppStore } from '@/store/appStore';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Calendar, Clock, FolderKanban, Users } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';

const formatTime = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const COLORS = ['#22D3EE', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass rounded-lg p-3">
        <p className="text-sm text-white font-medium">{payload[0].payload.name}</p>
        <p className="text-xs text-cyan-400 font-['JetBrains_Mono']">
          {formatTime(payload[0].name === 'time' ? payload[0].value * 60 : payload[0].value)}
        </p>
      </div>
    );
  }
  return null;
};

export default function ReportsPage() {
  const { currentOrg, projects, fetchProjects, fetchTimeSummary } = useAppStore();
  const [summary, setSummary] = useState(null);
  const [selectedProject, setSelectedProject] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (currentOrg) {
      fetchProjects(currentOrg.id);
    }
  }, [currentOrg, fetchProjects]);

  const loadReport = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const filters = {};
      if (selectedProject !== 'all') filters.projectId = selectedProject;
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;
      const data = await fetchTimeSummary(currentOrg.id, filters);
      setSummary(data);
    } catch (err) {
      console.error('Failed to load report:', err);
    }
  }, [currentOrg, selectedProject, startDate, endDate, fetchTimeSummary]);

  useEffect(() => {
    if (currentOrg) {
      loadReport();
    }
  }, [currentOrg, loadReport]);

  const projectChartData = summary
    ? Object.entries(summary.by_project).map(([id, data]) => ({
        name: data.name,
        time: Math.round(data.total_seconds / 60),
      }))
    : [];

  const userChartData = summary
    ? Object.entries(summary.by_user).map(([id, data]) => ({
        name: data.name,
        value: data.total_seconds,
      }))
    : [];

  if (!currentOrg) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <p className="text-zinc-400">Select an organization to view reports</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="reports-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Manrope']">Reports</h1>
          <p className="text-zinc-400 text-sm mt-1">Time tracking insights and analytics</p>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div>
          <label className="block text-sm text-zinc-400 mb-1.5">Project</label>
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="report-project-filter">
              <SelectValue placeholder="All Projects" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm text-zinc-400 mb-1.5">Start Date</label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="bg-zinc-900 border-zinc-800"
            data-testid="report-start-date"
          />
        </div>
        <div>
          <label className="block text-sm text-zinc-400 mb-1.5">End Date</label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="bg-zinc-900 border-zinc-800"
            data-testid="report-end-date"
          />
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-cyan-400" />
            </div>
            <span className="text-zinc-400 text-sm">Total Time</span>
          </div>
          <p className="text-2xl font-bold text-white font-['JetBrains_Mono']" data-testid="total-time-stat">
            {summary ? formatTime(summary.total_seconds) : '0h 0m'}
          </p>
        </div>

        <div className="stat-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-violet-500/10 rounded-lg flex items-center justify-center">
              <FolderKanban className="w-5 h-5 text-violet-400" />
            </div>
            <span className="text-zinc-400 text-sm">Projects</span>
          </div>
          <p className="text-2xl font-bold text-white font-['JetBrains_Mono']" data-testid="projects-count-stat">
            {summary ? Object.keys(summary.by_project).length : 0}
          </p>
        </div>

        <div className="stat-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-400" />
            </div>
            <span className="text-zinc-400 text-sm">Contributors</span>
          </div>
          <p className="text-2xl font-bold text-white font-['JetBrains_Mono']" data-testid="contributors-stat">
            {summary ? Object.keys(summary.by_user).length : 0}
          </p>
        </div>

        <div className="stat-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-amber-400" />
            </div>
            <span className="text-zinc-400 text-sm">Tasks Tracked</span>
          </div>
          <p className="text-2xl font-bold text-white font-['JetBrains_Mono']" data-testid="tasks-tracked-stat">
            {summary ? Object.keys(summary.by_task).length : 0}
          </p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Time by Project */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-6">Time by Project</h3>
          {projectChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={projectChartData}>
                <XAxis dataKey="name" stroke="#71717A" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#71717A" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}m`} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="time" fill="#22D3EE" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-zinc-500">
              No data available
            </div>
          )}
        </div>

        {/* Time by User */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-6">Time by User</h3>
          {userChartData.length > 0 ? (
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={userChartData}
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {userChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-zinc-500">
              No data available
            </div>
          )}
          {userChartData.length > 0 && (
            <div className="flex flex-wrap gap-4 justify-center mt-4">
              {userChartData.map((user, i) => (
                <div key={user.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  <span className="text-xs text-zinc-400">{user.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tasks Table */}
      {summary && Object.keys(summary.by_task).length > 0 && (
        <div className="mt-8 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="p-6 border-b border-zinc-800">
            <h3 className="text-lg font-semibold text-white">Task Breakdown</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left text-xs text-zinc-400 font-medium p-4">Task</th>
                  <th className="text-right text-xs text-zinc-400 font-medium p-4">Time Logged</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(summary.by_task).map(([id, task]) => (
                  <tr key={id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                    <td className="p-4 text-sm text-white">{task.title}</td>
                    <td className="p-4 text-sm text-cyan-400 font-['JetBrains_Mono'] text-right">
                      {formatTime(task.total_seconds)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
